import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Tag,
  Button,
  message,
  Space,
  Empty,
  Badge,
  Tooltip,
  Card,
  Statistic,
  Row,
  Col
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import api from '../../../axiosConfig';
import moment from 'moment';
import CrearNovedadModal from './CrearNovedadModal';
import SeguimientoNovedadModal from './SeguimientoNovedadModal';

const NovedadesModal = ({ visible, onClose, controlServicio }) => {
  const [novedades, setNovedades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [crearModalVisible, setCrearModalVisible] = useState(false);
  const [seguimientoModalVisible, setSeguimientoModalVisible] = useState(false);
  const [novedadSeleccionada, setNovedadSeleccionada] = useState(null);

  useEffect(() => {
    if (visible && controlServicio) {
      fetchNovedades();
    }
  }, [visible, controlServicio]);

  const fetchNovedades = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/novedades-control-servicio/control/${controlServicio.id}`);
      if (response.data.success) {
        setNovedades(response.data.data);
      }
    } catch (error) {
      message.error('Error al cargar novedades');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAbrirSeguimiento = (novedad) => {
    setNovedadSeleccionada(novedad);
    setSeguimientoModalVisible(true);
  };

  const handleCerrarSeguimiento = () => {
    setSeguimientoModalVisible(false);
    setNovedadSeleccionada(null);
  };

  const handleNovedadCreada = () => {
    fetchNovedades();
  };

  const handleNovedadActualizada = () => {
    fetchNovedades();
  };

  const tipoNovedadColors = {
    documentacion_incompleta: 'orange',
    datos_incorrectos: 'red',
    firma_ilegible: 'purple',
    firma_ilegible_paciente: 'magenta',
    otro: 'default'
  };

  const tipoNovedadLabels = {
    documentacion_incompleta: '📄 Documentación Incompleta',
    datos_incorrectos: '❌ Datos Incorrectos',
    firma_ilegible: '✍️ Firma Ilegible',
    firma_ilegible_paciente: '✍️ Firma Ilegible del Paciente (OTP)',
    otro: '⚠️ Otro'
  };

  const estadoNovedadColors = {
    pendiente: 'default',
    en_revision: 'processing',
    resuelta: 'success',
    cerrada: 'error'
  };

  const estadoNovedadLabels = {
    pendiente: 'Pendiente',
    en_revision: 'En Revisión',
    resuelta: 'Resuelta',
    cerrada: 'Cerrada'
  };

  const estadoNovedadIcons = {
    pendiente: <ClockCircleOutlined />,
    en_revision: <SyncOutlined spin />,
    resuelta: <CheckCircleOutlined />,
    cerrada: <ExclamationCircleOutlined />
  };

  // Calcular estadísticas
  const stats = {
    total: novedades.length,
    pendientes: novedades.filter(n => n.estado === 'pendiente').length,
    en_revision: novedades.filter(n => n.estado === 'en_revision').length,
    resueltas: novedades.filter(n => n.estado === 'resuelta').length,
    cerradas: novedades.filter(n => n.estado === 'cerrada').length
  };

  const columns = [
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 140,
      render: (estado) => (
        <Tag icon={estadoNovedadIcons[estado]} color={estadoNovedadColors[estado]}>
          {estadoNovedadLabels[estado]}
        </Tag>
      ),
      sorter: (a, b) => {
        const order = ['pendiente', 'en_revision', 'resuelta', 'cerrada'];
        return order.indexOf(a.estado) - order.indexOf(b.estado);
      }
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo_novedad',
      key: 'tipo_novedad',
      width: 220,
      render: (tipo) => (
        <Tag color={tipoNovedadColors[tipo]}>
          {tipoNovedadLabels[tipo]}
        </Tag>
      ),
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip title={text} placement="topLeft">
          {text}
        </Tooltip>
      ),
    },
    {
      title: 'Reportado Por',
      dataIndex: 'usuario',
      key: 'usuario',
      width: 150,
      render: (usuario) => {
        if (!usuario) return 'Sistema';
        const nombre = usuario.nombre || '';
        const apellido = usuario.apellido || '';
        return `${nombre}${apellido ? ' ' + apellido : ''}`.trim() || 'Usuario';
      },
    },
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (fecha) => moment(fecha).format('DD/MM/YY'),
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Ver Seguimiento y Gestión">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleAbrirSeguimiento(record)}
          >
            Ver
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          <Space size="large">
            <Space>
              <ExclamationCircleOutlined style={{ fontSize: '20px', color: '#ff4d4f' }} />
              <span style={{ fontSize: '16px' }}>Gestión de Novedades</span>
            </Space>
            {controlServicio && (
              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                {controlServicio.codigo_pcs}
              </Tag>
            )}
          </Space>
        }
        open={visible}
        onCancel={onClose}
        width={1200}
        footer={[
          <Button key="close" onClick={onClose} size="large">
            Cerrar
          </Button>
        ]}
        style={{ top: 20 }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Información del Control Servicio */}
          {controlServicio && (
            <Card size="small" style={{ backgroundColor: '#f5f5f5' }}>
              <Row gutter={16}>
                <Col span={12}>
                  <strong>Paciente:</strong> {controlServicio.nombre_paciente}
                </Col>
                <Col span={12}>
                  <strong>Documento:</strong> {controlServicio.numero_documento_paciente}
                </Col>
              </Row>
            </Card>
          )}

          {/* Estadísticas */}
          {stats.total > 0 && (
            <Row gutter={16}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Total"
                    value={stats.total}
                    prefix={<ExclamationCircleOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Pendientes"
                    value={stats.pendientes}
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#8c8c8c' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="En Revisión"
                    value={stats.en_revision}
                    prefix={<SyncOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="Resueltas"
                    value={stats.resueltas}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* Botón para crear nueva novedad */}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCrearModalVisible(true)}
            size="large"
            style={{ width: '100%' }}
          >
            Reportar Nueva Novedad
          </Button>

          {/* Tabla de novedades */}
          <Table
            columns={columns}
            dataSource={novedades}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showTotal: (total) => `Total: ${total} novedades`,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '20', '50']
            }}
            size="middle"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Space direction="vertical">
                      <span>No hay novedades registradas</span>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setCrearModalVisible(true)}
                      >
                        Crear Primera Novedad
                      </Button>
                    </Space>
                  }
                />
              )
            }}
            scroll={{ x: 1000 }}
          />
        </Space>
      </Modal>

      {/* Modal Crear Novedad */}
      <CrearNovedadModal
        visible={crearModalVisible}
        onClose={() => setCrearModalVisible(false)}
        onCreated={handleNovedadCreada}
        controlServicio={controlServicio}
      />

      {/* Modal Seguimiento */}
      <SeguimientoNovedadModal
        visible={seguimientoModalVisible}
        onClose={handleCerrarSeguimiento}
        onUpdated={handleNovedadActualizada}
        novedad={novedadSeleccionada}
      />
    </>
  );
};

export default NovedadesModal;
